/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from "react";
import { 
  User, 
  Mail, 
  Phone,
  MapPin, 
  Calendar, 
  Users, 
  CheckCircle, 
  FileCheck, 
  Landmark, 
  Award, 
  Heart, 
  ArrowRight, 
  Camera, 
  Printer, 
  RotateCcw, 
  Check, 
  CalendarDays,
  Compass
} from "lucide-react";
import type { BookingInquiry } from "../types";

const ROSE_WAITLIST_API_PATH = "/api/booking/rose-waitlist";
const ROSE_PRODUCTION_API_BASE = "https://loveandphotos.com";
const ROSE_PUBLIC_HOSTS = new Set(["therosephoto.com", "www.therosephoto.com"]);

function getRoseWaitlistApiBase() {
  const configuredApiBase = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
  if (configuredApiBase) return configuredApiBase;

  if (typeof window !== "undefined" && ROSE_PUBLIC_HOSTS.has(window.location.hostname)) {
    return ROSE_PRODUCTION_API_BASE;
  }

  return "";
}

function formatRoseDate(dateValue: string) {
  const match = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return dateValue;

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });
}

export default function InquiryAtelier() {
  const [step, setStep] = useState<1 | 2 | 3 | "submitted">(1);
  const aestheticMode: string = "vogue";
  
  const [activeInputFocus, setActiveInputFocus] = useState<string | null>(null);

  const [form, setForm] = useState<BookingInquiry>({
    fullName: "",
    email: "",
    phone: "",
    partnerName: "",
    weddingDate: "",
    destination: "",
    guestScale: "intimate",
    atelierNotes: "",
    howWeMeet: "",
    photographyVision: "",
    weddingCityCountry: "",
    plannerReferral: "",
    wardrobeDetails: "",
    smsTransactionalConsent: false,
    smsMarketingConsent: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registryNumber, setRegistryNumber] = useState("");

  // Auto scroll within the element for smooth transitions
  useEffect(() => {
    const section = document.getElementById("inquiry-section");
    if (section && step !== 1) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [step]);

  const validateStep1 = () => {
    const err: Record<string, string> = {};
    if (!form.fullName.trim()) err.fullName = "Commission applicant name required.";
    if (!form.partnerName.trim()) err.partnerName = "Significant other's name required.";
    if (!form.email.trim() || !form.email.includes("@")) {
      err.email = "Valid electronic mail coordinate required.";
    }
    if (!form.howWeMeet.trim()) {
      err.howWeMeet = "Luxury origin backstory required.";
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const validateStep2 = () => {
    const err: Record<string, string> = {};
    if (!form.weddingDate) err.weddingDate = "Specific ceremony calendar date requested.";
    if (!form.destination.trim()) {
      err.destination = "Celebration location / venue coordinates required.";
    }
    if (!form.weddingCityCountry.trim()) {
      err.weddingCityCountry = "Wedding city & country of celebrations required.";
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const validateStep3 = () => {
    const err: Record<string, string> = {};
    if (!form.photographyVision.trim()) {
      err.photographyVision = "Photography vision required.";
    }
    if (!form.smsTransactionalConsent) {
      err.smsTransactionalConsent = "Please agree to receive appointment reminder and confirmation text messages.";
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handlePrev = () => {
    if (step === 2) setStep(1);
    if (step === 3) setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep3()) return;

    setSubmitError("");
    setIsSubmitting(true);

    try {
      const apiBase = getRoseWaitlistApiBase();
      const response = await fetch(`${apiBase}${ROSE_WAITLIST_API_PATH}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          ghlFormId: "6cBi1wsmxh0Gv7bTnw2d",
        }),
      });

      const responseText = await response.text();
      const result = responseText ? JSON.parse(responseText) : {};

      if (!response.ok || !result.success) {
        if (!result.message) {
          console.error("Rose waitlist submission returned an unexpected response:", {
            apiBase: apiBase || window.location.origin,
            status: response.status,
            contentType: response.headers.get("content-type"),
            bodyPreview: responseText.slice(0, 240),
          });
        }

        throw new Error(result.message || `We could not register your waitlist request. The intake service returned ${response.status}.`);
      }

      setRegistryNumber(result.registryNumber || `TRP-2026-${Math.floor(Math.random() * 8888 + 1111)}`);
      setStep("submitted");
    } catch (error) {
      console.error("Rose waitlist submission failed:", error);
      setSubmitError(
        error instanceof TypeError
          ? "We could not reach the Rose intake service. Please try again in a moment."
          : error instanceof SyntaxError
            ? "The Rose intake service returned an unexpected response. Please try again in a moment."
          : error instanceof Error
            ? error.message
            : "We could not register your waitlist request. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to pre-fill dynamic seasonal date options
  const setSeasonalDate = (yearOffset: number, season: "Spring" | "Summer" | "Autumn" | "Winter") => {
    const year = 2027 + yearOffset;
    let monthDay = "06-20";
    if (season === "Spring") monthDay = "04-18";
    if (season === "Autumn") monthDay = "10-10";
    if (season === "Winter") monthDay = "12-19";
    
    setForm({ ...form, weddingDate: `${year}-${monthDay}` });
  };

  return (
    <section 
      className={`py-24 px-4 sm:px-6 transition-colors duration-1000 border-t ${
        aestheticMode === "gucci" 
          ? "bg-[#F3EFE9] text-[#2C2421] border-[#8A7D6B]/30" 
          : "bg-[#F5F2ED] text-[#1A1A1A] border-[#1A1A1A]"
      }`} 
      id="inquiry-section"
    >
      <div className="max-w-7xl mx-auto">
        
        {/* Editorial Waitlist Header */}
        <div className="text-center mb-16 relative">
          <span className="text-[10px] tracking-[0.35em] uppercase font-display flex items-center justify-center gap-1.5 mb-4 text-[#c4a482]">
            <Landmark size={11} /> Bespoke Global Commissions
          </span>
          
          <h2 className="font-serif text-[44px] sm:text-[62px] font-extralight tracking-tight leading-none text-[#1A1A1A]">
            Join the Waitlist
          </h2>
          
          <p className="font-serif font-light text-[11px] uppercase tracking-[0.25em] mx-auto max-w-lg mt-4 text-[#7f7d78]">
            Rome • Paris • NYC
          </p>

          <div className="w-28 h-[1px] mx-auto my-6 bg-[#1A1A1A]/40"></div>

          <p className="font-sans font-light text-xs max-w-xl mx-auto leading-relaxed uppercase tracking-wider text-[#7f7d78]">
            Admissions are strictly by waitlist review. To preserve our highly personal experience and meticulous focus, we accept only a small collection of wedding commissions each calendar year. There is no direct booking. If we have a vacancy and see stylistic compatibility, you'll be invited to a private 1-on-1 style consultation.
          </p>
        </div>

        {/* Dynamic Split Layout: Interactive inputs left, gorgeous Live Ledger Preview right */}
        {step !== "submitted" ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            
            {/* COLUMN 1: INTERACTIVE COMMISSION FORM (7 Columns) */}
            <div className="lg:col-span-7">
              <div className={`border p-6 sm:p-10 relative transition-all duration-700 ${
                aestheticMode === "gucci" 
                  ? "border-2 border-[#8A7D6B]/50 bg-[#FCFAF7] shadow-xl" 
                  : "border border-[#1A1A1A] bg-[#E8E4DF]/30"
              }`}>
                {/* Antique corner brackets for Gucci, simple thin overlays for Vogue */}
                {aestheticMode === "gucci" && (
                  <>
                    <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-[#8A7D6B]/40" />
                    <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-[#8A7D6B]/40" />
                    <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-[#8A7D6B]/40" />
                    <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-[#8A7D6B]/40" />
                  </>
                )}
                
                <div className="grain-overlay absolute inset-0 opacity-15"></div>

                {/* Progress Indicators */}
                <div className="flex justify-between items-center mb-8 z-10 relative">
                  <div>
                    <span className={`text-[9px] uppercase tracking-[0.2em] font-medium block ${
                      aestheticMode === "gucci" ? "text-[#a47a50]" : "text-[#c4a482]"
                    }`}>
                      Current Phase
                    </span>
                    <h4 className="font-serif text-[18px] font-light">
                      {step === 1 && "I. Client Portrait"}
                      {step === 2 && "II. Celebration Blueprint"}
                      {step === 3 && "III. Commission Details"}
                    </h4>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-[10px] font-mono tracking-widest text-neutral-500">
                      0{step} / 03
                    </span>
                    <div className="flex space-x-1.5">
                      {[1, 2, 3].map((s) => (
                        <span
                          key={s}
                          className={`h-1.5 rounded-full transition-all duration-500 ${
                            step === s 
                              ? (aestheticMode === "gucci" ? "bg-[#2C2421] w-6" : "bg-[#1A1A1A] w-6") 
                              : "bg-black/10 w-2"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="z-10 relative space-y-6">
                  
                  {/* STEP 1: Personal Coordinates */}
                  {step === 1 && (
                    <div className="space-y-6 animate-fade-in">
                      <div className="border-b border-black/5 pb-4">
                        <h3 className="font-serif text-[22px] font-light leading-snug">
                          The Celebrated Partners
                        </h3>
                        <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-light mt-1">
                          Please register both names for the historical press log.
                        </p>
                      </div>

                      <div className="space-y-5">
                        {/* Full Name Applicant */}
                        <div className="space-y-1">
                          <label className="flex justify-between items-center text-[10px] uppercase tracking-widest text-[#1A1A1A]/80 font-semibold">
                            <span>Your Full Name *</span>
                            {form.fullName.length > 2 && <span className="text-emerald-700 flex items-center gap-0.5 text-[8spx] normal-case"><Check size={10} /> Saved</span>}
                          </label>
                          <div className={`relative border transition-all duration-300 ${
                            activeInputFocus === "fullName" 
                              ? (aestheticMode === "gucci" ? "border-[#2C2421]" : "border-[#1A1A1A]") 
                              : "border-[#1A1A1A]/15"
                          }`}>
                            <span className="absolute left-3 top-3.5 text-neutral-400">
                              <User size={13} className="transition-transform duration-300 group-focus:scale-110" />
                            </span>
                            <input
                              id="input-fullname"
                              type="text"
                              required
                              value={form.fullName}
                              onFocus={() => setActiveInputFocus("fullName")}
                              onBlur={() => setActiveInputFocus(null)}
                              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                              placeholder="e.g. Charlotte Rose"
                              className={`w-full text-xs font-serif italic py-3.5 pl-9 pr-4 bg-transparent focus:ring-0 focus:outline-none rounded-none placeholder-neutral-400 ${
                                aestheticMode === "gucci" ? "text-[#2C2421]" : "text-[#1A1A1A]"
                              }`}
                            />
                          </div>
                          {errors.fullName && <p className="text-[9.5px] text-red-600 font-medium mt-1">{errors.fullName}</p>}
                        </div>

                        {/* Partner Name */}
                        <div className="space-y-1">
                          <label className="flex justify-between items-center text-[10px] uppercase tracking-widest text-[#1A1A1A]/80 font-semibold">
                            <span>Significant Other's Full Name *</span>
                            {form.partnerName.length > 2 && <span className="text-emerald-700 flex items-center gap-0.5"><Check size={10} /> Saved</span>}
                          </label>
                          <div className={`relative border transition-all duration-300 ${
                            activeInputFocus === "partnerName" 
                              ? (aestheticMode === "gucci" ? "border-[#2C2421]" : "border-[#1A1A1A]") 
                              : "border-[#1A1A1A]/15"
                          }`}>
                            <span className="absolute left-3 top-3.5 text-neutral-400">
                              <User size={13} />
                            </span>
                            <input
                              id="input-partnername"
                              type="text"
                              required
                              value={form.partnerName}
                              onFocus={() => setActiveInputFocus("partnerName")}
                              onBlur={() => setActiveInputFocus(null)}
                              onChange={(e) => setForm({ ...form, partnerName: e.target.value })}
                              placeholder="e.g. Julian Sterling"
                              className={`w-full text-xs font-serif italic py-3.5 pl-9 pr-4 bg-transparent focus:ring-0 focus:outline-none rounded-none placeholder-neutral-400 ${
                                aestheticMode === "gucci" ? "text-[#2C2421]" : "text-[#1A1A1A]"
                              }`}
                            />
                          </div>
                          {errors.partnerName && <p className="text-[9.5px] text-red-600 font-medium mt-1">{errors.partnerName}</p>}
                        </div>

                        {/* Email Coord */}
                        <div className="space-y-1">
                          <label className="flex justify-between items-center text-[10px] uppercase tracking-widest text-[#1A1A1A]/80 font-semibold">
                            <span>Electronic Mail coordinates *</span>
                            {form.email.includes("@") && <span className="text-emerald-700 flex items-center gap-0.5"><Check size={10} /> Validated</span>}
                          </label>
                          <div className={`relative border transition-all duration-300 ${
                            activeInputFocus === "email" 
                              ? (aestheticMode === "gucci" ? "border-[#2C2421]" : "border-[#1A1A1A]") 
                              : "border-[#1A1A1A]/15"
                          }`}>
                            <span className="absolute left-3 top-3.5 text-neutral-400">
                              <Phone size={13} />
                            </span>
                            <input
                              id="input-email"
                              type="email"
                              required
                              value={form.email}
                              onFocus={() => setActiveInputFocus("email")}
                              onBlur={() => setActiveInputFocus(null)}
                              onChange={(e) => setForm({ ...form, email: e.target.value })}
                              placeholder="luxurious@example.com"
                              className="w-full text-xs font-mono py-3.5 pl-9 pr-4 bg-transparent focus:ring-0 focus:outline-none rounded-none placeholder-neutral-400 text-stone-800"
                            />
                          </div>
                          {errors.email && <p className="text-[9.5px] text-red-600 font-medium mt-1">{errors.email}</p>}
                        </div>

                        {/* Phone Coord */}
                        <div className="space-y-1">
                          <label className="flex justify-between items-center text-[10px] uppercase tracking-widest text-[#1A1A1A]/80 font-semibold">
                            <span>Phone Coordinates (Optional)</span>
                            {form.phone.length > 6 && <span className="text-emerald-700 flex items-center gap-0.5"><Check size={10} /> Saved</span>}
                          </label>
                          <div className={`relative border transition-all duration-300 ${
                            activeInputFocus === "phone"
                              ? (aestheticMode === "gucci" ? "border-[#2C2421]" : "border-[#1A1A1A]")
                              : "border-[#1A1A1A]/15"
                          }`}>
                            <span className="absolute left-3 top-3.5 text-neutral-400">
                              <Mail size={13} />
                            </span>
                            <input
                              id="input-phone"
                              type="tel"
                              value={form.phone}
                              onFocus={() => setActiveInputFocus("phone")}
                              onBlur={() => setActiveInputFocus(null)}
                              onChange={(e) => setForm({ ...form, phone: e.target.value })}
                              placeholder="(555) 123-4567"
                              className="w-full text-xs font-mono py-3.5 pl-9 pr-4 bg-transparent focus:ring-0 focus:outline-none rounded-none placeholder-neutral-400 text-stone-800"
                            />
                          </div>
                        </div>

                        {/* How did you two meet */}
                        <div className="space-y-1">
                          <label className="flex justify-between items-center text-[10px] uppercase tracking-widest text-[#1A1A1A]/80 font-semibold text-left">
                            <span>How did you two meet? *</span>
                          </label>
                          <textarea
                            id="input-howWeMeet"
                            rows={3}
                            required
                            value={form.howWeMeet}
                            onChange={(e) => setForm({ ...form, howWeMeet: e.target.value })}
                            placeholder="Tell us a piece of your romantic story (how your paths crossed, key milestones)..."
                            className="w-full text-xs font-serif italic p-3.5 border border-[#1A1A1A]/15 bg-transparent placeholder-neutral-400 focus:outline-none focus:ring-0 focus:border-[#1A1A1A] rounded-none resize-none duration-300 text-stone-800"
                          />
                          {errors.howWeMeet && <p className="text-[9.5px] text-red-600 font-medium mt-1">{errors.howWeMeet}</p>}
                        </div>

                      </div>
                    </div>
                  )}

                  {/* STEP 2: Location, Venue & Celebrated Date */}
                  {step === 2 && (
                    <div className="space-y-6 animate-fade-in">
                      <div className="border-b border-black/5 pb-4">
                        <h3 className="font-serif text-[22px] font-light leading-snug">
                          The Timeline & Haven
                        </h3>
                        <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-light mt-1">
                          Specify your exquisite destination venue & expected solar calendar coordinates.
                        </p>
                      </div>

                      <div className="space-y-5">
                        {/* Destination Locale / Venue */}
                        <div className="space-y-1">
                          <label className="flex justify-between items-center text-[10px] uppercase tracking-widest text-[#1A1A1A]/80 font-semibold">
                            <span>Celebration Venue & Destination *</span>
                            {form.destination.length > 3 && <span className="text-emerald-700 flex items-center gap-0.5"><Check size={10} /> Location Set</span>}
                          </label>
                          <div className={`relative border transition-all duration-300 ${
                            activeInputFocus === "destination" 
                              ? (aestheticMode === "gucci" ? "border-[#2C2421]" : "border-[#1A1A1A]") 
                              : "border-[#1A1A1A]/15"
                          }`}>
                            <span className="absolute left-3 top-3.5 text-neutral-400">
                              <MapPin size={13} />
                            </span>
                            <input
                              id="input-destination"
                              type="text"
                              required
                              value={form.destination}
                              onFocus={() => setActiveInputFocus("destination")}
                              onBlur={() => setActiveInputFocus(null)}
                              onChange={(e) => setForm({ ...form, destination: e.target.value })}
                              placeholder="e.g. Villa d'Este, Lake Como, Italy"
                              className={`w-full text-xs font-serif italic py-3.5 pl-9 pr-4 bg-transparent focus:ring-0 focus:outline-none rounded-none placeholder-neutral-400 ${
                                aestheticMode === "gucci" ? "text-[#2C2421]" : "text-[#1A1A1A]"
                              }`}
                            />
                          </div>
                          <span className="text-[8.5px] text-neutral-400 block tracking-widest uppercase">
                            Global deployments include luxury logistics planning.
                          </span>
                          {errors.destination && <p className="text-[9.5px] text-red-656 font-medium mt-1">{errors.destination}</p>}
                        </div>

                        {/* Wedding Date Picker */}
                        <div className="space-y-2">
                          <label className="block text-[10px] uppercase tracking-widest text-[#1A1A1A]/80 font-semibold">
                            Marriage Calendar Date *
                          </label>
                          <div className={`relative border transition-all duration-300 ${
                            activeInputFocus === "weddingDate" 
                              ? (aestheticMode === "gucci" ? "border-[#2C2421]" : "border-[#1A1A1A]") 
                              : "border-[#1A1A1A]/15"
                          }`}>
                            <span className="absolute left-3 top-3.5 text-neutral-400">
                              <CalendarDays size={13} />
                            </span>
                            <input
                              id="input-weddingdate"
                              type="date"
                              required
                              min="2027-01-01"
                              value={form.weddingDate}
                              onFocus={() => setActiveInputFocus("weddingDate")}
                              onBlur={() => setActiveInputFocus(null)}
                              onChange={(e) => setForm({ ...form, weddingDate: e.target.value })}
                              className="w-full text-xs font-mono py-3 pl-9 pr-4 bg-transparent focus:ring-0 focus:outline-none rounded-none text-stone-800 dark:[color-scheme:light]"
                            />
                          </div>
                          {errors.weddingDate && <p className="text-[9.5px] text-red-656 font-medium mt-1">{errors.weddingDate}</p>}
                        </div>

                        {/* Quick Interactive Season Select Tool */}
                        <div className="space-y-1 pt-1.5">
                          <label className="block text-[8.5px] tracking-[0.2em] uppercase text-neutral-400">
                            Quick Season Planner Blueprint
                          </label>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 pt-1">
                            {(["Spring", "Summer", "Autumn", "Winter"] as const).map((season) => (
                              <button
                                key={season}
                                type="button"
                                onClick={() => setSeasonalDate(0, season)}
                                className={`py-2 text-[8.5px] tracking-widest uppercase border transition duration-300 cursor-pointer text-center ${
                                  form.weddingDate.includes(season === "Spring" ? "04-18" : season === "Autumn" ? "10-10" : season === "Winter" ? "12-19" : "06-20")
                                    ? "bg-black text-white border-black"
                                    : "bg-white/20 border-black/10 hover:border-black text-neutral-600"
                                }`}
                              >
                                {season} 2027
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* City & Country */}
                        <div className="space-y-1">
                          <label className="flex justify-between items-center text-[10px] uppercase tracking-widest text-[#1A1A1A]/80 font-semibold text-left">
                            <span>What city & country is your wedding in? *</span>
                            {form.weddingCityCountry.length > 2 && <span className="text-emerald-700 flex items-center gap-0.5"><Check size={10} /> Set</span>}
                          </label>
                          <div className={`relative border transition-all duration-300 ${
                            activeInputFocus === "weddingCityCountry" 
                              ? "border-[#1A1A1A]" 
                              : "border-[#1A1A1A]/15"
                          }`}>
                            <span className="absolute left-3 top-3.5 text-neutral-400 font-sans text-xs">
                              🌍
                            </span>
                            <input
                              id="input-weddingCityCountry"
                              type="text"
                              required
                              value={form.weddingCityCountry}
                              onFocus={() => setActiveInputFocus("weddingCityCountry")}
                              onBlur={() => setActiveInputFocus(null)}
                              onChange={(e) => setForm({ ...form, weddingCityCountry: e.target.value })}
                              placeholder="e.g. Paris, France or New York, USA"
                              className="w-full text-[#1A1A1A] text-xs font-serif italic py-3.5 pl-9 pr-4 bg-transparent focus:ring-0 focus:outline-none rounded-none placeholder-neutral-400"
                            />
                          </div>
                          {errors.weddingCityCountry && <p className="text-[9.5px] text-red-600 font-medium mt-1">{errors.weddingCityCountry}</p>}
                        </div>

                        {/* Planner Referral */}
                        <div className="space-y-1">
                          <label className="flex justify-between items-center text-[10px] uppercase tracking-widest text-[#1A1A1A]/80 font-semibold text-left">
                            <span>Bespoke Planner or Coordinating Atelier (Optional)</span>
                          </label>
                          <div className={`relative border transition-all duration-300 ${
                            activeInputFocus === "plannerReferral" 
                              ? "border-[#1A1A1A]" 
                              : "border-[#1A1A1A]/15"
                          }`}>
                            <span className="absolute left-3 top-3.5 text-neutral-400 font-sans text-xs">
                              🏰
                            </span>
                            <input
                              id="input-plannerReferral"
                              type="text"
                              value={form.plannerReferral}
                              onFocus={() => setActiveInputFocus("plannerReferral")}
                              onBlur={() => setActiveInputFocus(null)}
                              onChange={(e) => setForm({ ...form, plannerReferral: e.target.value })}
                              placeholder="e.g. Mindy Weiss, Laurie Arons, or self-planned..."
                              className="w-full text-[#1A1A1A] text-xs font-serif italic py-3.5 pl-9 pr-4 bg-transparent focus:ring-0 focus:outline-none rounded-none placeholder-neutral-400"
                            />
                          </div>
                        </div>

                      </div>
                    </div>
                  )}

                  {/* STEP 3: Tiers, Scaling, & Brief Custom Message */}
                  {step === 3 && (
                    <div className="space-y-6 animate-fade-in">
                      <div className="border-b border-black/5 pb-4">
                        <h3 className="font-serif text-[22px] font-light leading-snug">
                          The Artistic Intention
                        </h3>
                        <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-light mt-1">
                          Align your estimated guest population, visual preferences, and key message directives.
                        </p>
                      </div>

                      <div className="space-y-5">
                        {/* Guest scale selector */}
                        <div className="space-y-2">
                          <label className="block text-[10px] uppercase tracking-widest text-[#1A1A1A]/80 font-semibold mb-1">
                            Ceremony Presence Scale *
                          </label>
                          <div className="grid grid-cols-3 gap-2">
                            {([
                              { key: "intimate", label: "Intimate (<50)" },
                              { key: "grand", label: "Grand (<150)" },
                              { key: "exclusive", label: "Elite (150+)" },
                            ] as const).map((g) => (
                              <button
                                key={g.key}
                                type="button"
                                onClick={() => setForm({ ...form, guestScale: g.key })}
                                className={`py-3 text-[9px] uppercase tracking-widest border transition duration-300 cursor-pointer ${
                                  form.guestScale === g.key
                                    ? (aestheticMode === "gucci" ? "border-[#2C2421] bg-[#2C2421] text-[#F3EFE9]" : "border-[#1A1A1A] bg-[#1A1A1A] text-[#F5F2ED]")
                                    : "border-black/10 bg-white/30 text-neutral-600 hover:border-black/40"
                                }`}
                              >
                                {g.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Photography Vision */}
                        <div className="space-y-1">
                          <label className="block text-[10px] uppercase tracking-widest text-[#1A1A1A]/80 font-semibold">
                            Tell us about your vision for the photography *
                          </label>
                          <textarea
                            id="input-photographyVision"
                            rows={3}
                            required
                            value={form.photographyVision}
                            onChange={(e) => setForm({ ...form, photographyVision: e.target.value })}
                            placeholder="Describe your ideal look and feel (e.g. moody, candid-driven, editorial lifestyle plates, classic romance)..."
                            className="w-full text-xs font-serif italic p-3 border border-[#1A1A1A]/15 bg-transparent placeholder-neutral-400 focus:outline-none focus:ring-0 focus:border-[#1A1A1A] rounded-none resize-none duration-300 text-stone-800"
                          />
                          {errors.photographyVision && <p className="text-[9.5px] text-red-600 font-medium mt-1">{errors.photographyVision}</p>}
                        </div>

                        {/* Planned Wardrobe & Couturier */}
                        <div className="space-y-1">
                          <label className="block text-[10px] uppercase tracking-widest text-[#1A1A1A]/80 font-semibold">
                            Planned Wardrobe & Couturier (Optional)
                          </label>
                          <input
                            id="input-wardrobeDetails"
                            type="text"
                            value={form.wardrobeDetails}
                            onChange={(e) => setForm({ ...form, wardrobeDetails: e.target.value })}
                            placeholder="e.g. Vivienne Westwood dress, custom Prada tailoring, vintage lace..."
                            className="w-full text-xs font-serif italic p-3 border border-[#1A1A1A]/15 bg-transparent placeholder-neutral-400 focus:outline-none focus:ring-0 focus:border-[#1A1A1A] rounded-none duration-300 text-stone-800"
                          />
                        </div>

                        {/* Special brief message */}
                        <div className="space-y-1">
                          <label className="block text-[10px] uppercase tracking-widest text-[#1A1A1A]/80 font-semibold">
                            Bespoke Styling Directives & Brief Message
                          </label>
                          <textarea
                            id="input-ateliernores"
                            rows={3}
                            value={form.atelierNotes}
                            onChange={(e) => setForm({ ...form, atelierNotes: e.target.value })}
                            placeholder="Share any structural cues, preferred attire codes (e.g. vintage tuxedo, classical lace), styling formulas or special requested dates."
                            className="w-full text-xs font-serif italic p-3 border border-[#1A1A1A]/15 bg-transparent placeholder-neutral-400 focus:outline-none focus:ring-0 focus:border-[#1A1A1A] rounded-none resize-none duration-300"
                          />
                        </div>

                        <div className="space-y-4 pt-3">
                          <label className="flex items-start gap-3 text-left cursor-pointer">
                            <input
                              id="input-smsTransactionalConsent"
                              type="checkbox"
                              checked={form.smsTransactionalConsent}
                              onChange={(e) => setForm({ ...form, smsTransactionalConsent: e.target.checked })}
                              className="mt-1 h-4 w-4 shrink-0 rounded-none border border-[#1A1A1A]/50 bg-transparent accent-[#1A1A1A]"
                            />
                            <span className="text-[10.5px] leading-relaxed text-neutral-600 font-sans">
                              By checking this box, I consent to receive non-marketing text messages from Love & Photos LLC DBA THE ROSE PHOTO about appointment reminders and confirmations. Message frequency varies, message & data rates may apply. Text HELP for assistance, reply STOP to opt out.
                            </span>
                          </label>
                          {errors.smsTransactionalConsent && (
                            <p className="text-[9.5px] text-red-600 font-medium -mt-2 ml-7">{errors.smsTransactionalConsent}</p>
                          )}

                          <label className="flex items-start gap-3 text-left cursor-pointer">
                            <input
                              id="input-smsMarketingConsent"
                              type="checkbox"
                              checked={form.smsMarketingConsent}
                              onChange={(e) => setForm({ ...form, smsMarketingConsent: e.target.checked })}
                              className="mt-1 h-4 w-4 shrink-0 rounded-none border border-[#1A1A1A]/50 bg-transparent accent-[#1A1A1A]"
                            />
                            <span className="text-[10.5px] leading-relaxed text-neutral-600 font-sans">
                              By checking this box, I consent to receive marketing and promotional messages including special offers, discounts, new product updates among others from Love & Photos LLC DBA THE ROSE PHOTO at the phone number provided. Frequency may vary. Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Navigation & Submission Controls */}
                  <div className="flex justify-between items-center pt-6 border-t border-black/5 mt-8">
                    {step > 1 ? (
                      <button
                        id="btn-prev-step"
                        type="button"
                        onClick={handlePrev}
                        className="border border-black/40 text-neutral-800 px-5 py-2.5 text-[10px] uppercase tracking-wider hover:bg-black hover:text-[#F5F2ED] transition duration-500 font-display cursor-pointer bg-transparent"
                      >
                        ← Phase 0{step - 1}
                      </button>
                    ) : (
                      <div />
                    )}

                    {step < 3 ? (
                      <button
                        id="btn-next-step"
                        type="button"
                        onClick={handleNext}
                        className={`px-7 py-3 text-[10px] uppercase tracking-[0.2em] font-display transition duration-500 cursor-pointer flex items-center gap-1.5 ${
                          aestheticMode === "gucci" 
                            ? "bg-[#2C2421] text-[#F3EFE9] hover:bg-black" 
                            : "bg-[#1A1A1A] text-[#F5F2ED] hover:bg-neutral-850"
                        }`}
                      >
                        <span>Exquisite Next Stage</span>
                        <ArrowRight size={11} />
                      </button>
                    ) : (
                      <button
                        id="btn-submit-inquiry"
                        type="submit"
                        disabled={isSubmitting}
                        className="bg-[#c4a482] text-white px-8 py-3.5 text-[10.5px] uppercase tracking-[0.25em] hover:bg-black transition duration-500 font-display cursor-pointer flex items-center space-x-1.5 border border-[#c4a482] disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <FileCheck size={13} />
                        <span>{isSubmitting ? "REGISTERING..." : "JOIN THE WAITLIST"}</span>
                      </button>
                    )}
                  </div>

                  {submitError && (
                    <p className="text-[10px] text-red-700 bg-red-50 border border-red-200 px-3 py-2 uppercase tracking-wider">
                      {submitError}
                    </p>
                  )}

                </form>
              </div>
            </div>

            {/* COLUMN 2: REAL-TIME EDITORIAL COMMISSION LEDGER PREVIEW (5 Columns) */}
            <div className="lg:col-span-12 xl:col-span-5 lg:sticky lg:top-28">
              <div className={`p-8 font-serif leading-relaxed relative ${
                aestheticMode === "gucci" 
                  ? "bg-[#FCFAF7] border-2 border-[#8A7D6B] text-[#2C2421] shadow-lg shadow-amber-900/5" 
                  : "bg-white border border-[#1A1A1A]/80 text-[#1A1A1A]"
              }`}>
                <div className="grain-overlay absolute inset-0 opacity-20"></div>

                {/* Ledger Watermark background */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none select-none">
                  <Landmark size={280} />
                </div>

                <div className="border-b border-[#1A1A1A]/10 pb-4 text-center">
                  <div className="inline-flex py-1 px-3 border border-[#c4a482]/30 text-[#c4a482] text-[8px] tracking-[0.3em] font-sans uppercase mb-2">
                    Official Drafting Roll
                  </div>
                  <h4 className="font-serif tracking-widest uppercase text-xs font-light text-center">
                    THE ROSE PHOTO HOUSE
                  </h4>
                  <p className="text-[8px] tracking-[0.4em] text-neutral-400 font-sans uppercase">
                    Vogue • Premium Digital Ledger
                  </p>
                </div>

                {/* Interactive Dynamic Data Highlights */}
                <div className="py-6 space-y-5 text-xs relative">
                  
                  {/* Status Indicator */}
                  <div className="flex justify-between items-baseline text-[8px] uppercase tracking-widest text-neutral-400 font-sans">
                    <span>Ledger State: Live Sync</span>
                    <span className="text-amber-700 animate-pulse font-semibold">Active Draft</span>
                  </div>

                  {/* names */}
                  <div className="relative border-b border-[#1A1A1A]/5 pb-3.5 pt-1.5">
                    <span className="block text-[8px] uppercase tracking-widest text-neutral-400 font-sans font-semibold mb-1">
                      Registered Cohorts
                    </span>
                    <div className="text-[14px] font-serif tracking-tight flex flex-col justify-start">
                      <span className="font-semibold not-italic">
                        {form.fullName || "— Applicant Name —"}
                      </span>
                      <span className="text-xs text-neutral-400 font-sans uppercase tracking-widest py-0.5">
                        &
                      </span>
                      <span className="italic font-light">
                        {form.partnerName || "— Partner Name —"}
                      </span>
                    </div>
                  </div>

                  {/* Date & Destination Venue */}
                  <div className="grid grid-cols-2 gap-4 border-b border-[#1A1A1A]/5 pb-3.5">
                    <div>
                      <span className="block text-[8px] uppercase tracking-widest text-neutral-400 font-sans font-semibold mb-1">
                        Calendar Matrix
                      </span>
                      <span className="font-mono text-[11px]">
                        {form.weddingDate ? formatRoseDate(form.weddingDate) : "Select Date"}
                      </span>
                    </div>
                    <div>
                      <span className="block text-[8px] uppercase tracking-widest text-neutral-400 font-sans font-semibold mb-1">
                        Ceremony coordinates
                      </span>
                      <span className="italic font-serif truncate text-[11px] block text-stone-700">
                        {form.destination || "Awaiting Location"}
                      </span>
                    </div>
                  </div>

                  {/* High-End Extra Fields in Ledger */}
                  {(form.howWeMeet || form.photographyVision || form.weddingCityCountry || form.wardrobeDetails || form.plannerReferral) && (
                    <div className="pt-2 border-t border-[#1A1A1A]/5 space-y-2 mt-2">
                      <span className="block text-[8px] uppercase tracking-widest text-neutral-400 font-sans font-semibold">
                        Luxury Dossier Logs
                      </span>
                      <div className="text-[10.5px] space-y-2 font-serif text-stone-800 bg-neutral-100/50 p-2.5 border border-black/5 rounded">
                        {form.weddingCityCountry && (
                          <div className="flex justify-between items-baseline text-[9px] gap-2">
                            <span className="font-sans uppercase text-[7px] text-neutral-400 shrink-0">Celebration City:</span>
                            <span className="font-semibold truncate">{form.weddingCityCountry}</span>
                          </div>
                        )}
                        {form.plannerReferral && (
                          <div className="flex justify-between items-baseline text-[9px] gap-2">
                            <span className="font-sans uppercase text-[7px] text-neutral-400 shrink-0">Bespoke Planner:</span>
                            <span className="italic truncate">{form.plannerReferral}</span>
                          </div>
                        )}
                        {form.wardrobeDetails && (
                          <div className="flex justify-between items-baseline text-[9px] gap-2">
                            <span className="font-sans uppercase text-[7px] text-neutral-400 shrink-0">Wardrobe:</span>
                            <span className="truncate">{form.wardrobeDetails}</span>
                          </div>
                        )}
                        {form.howWeMeet && (
                          <div className="pt-1.5 border-t border-black/5 text-[9px] italic">
                            <span className="font-sans uppercase text-[7px] text-[#c4a482] block not-italic font-bold">Origin Story:</span>
                            <span className="line-clamp-2">"{form.howWeMeet}"</span>
                          </div>
                        )}
                        {form.photographyVision && (
                          <div className="pt-1.5 border-t border-black/5 text-[9px] italic text-[#4A1722]">
                            <span className="font-sans uppercase text-[7px] text-[#4A1722] block not-italic font-bold">Photography Vision:</span>
                            <span className="line-clamp-2">"{form.photographyVision}"</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Client Styling Directives Message */}
                  <div className="pt-2">
                    <span className="block text-[8px] uppercase tracking-widest text-neutral-400 font-sans font-semibold mb-1">
                      Client Intent / Brief Message
                    </span>
                    <p className="p-2.5 bg-neutral-100/50 border border-black/5 text-[10px] leading-relaxed italic text-stone-800 max-h-24 overflow-y-auto font-serif">
                      {form.atelierNotes.trim() ? `"${form.atelierNotes}"` : "Input styling, wardrobe directives or specific visual references dynamically."}
                    </p>
                  </div>

                </div>

                {/* Fine Gucci ribbon footer or Vogue minimalist barcode */}
                {aestheticMode === "gucci" ? (
                  <div className="border-t border-[#8A7D6B]/30 pt-4 flex justify-between items-center text-[8px] uppercase tracking-[0.18em] text-[#8A7D6B]">
                    <span>GUCCI DESIGN CO-COLLAB</span>
                    <span>1 2 9 A P R — L E I C A</span>
                  </div>
                ) : (
                  <div className="border-t border-[#1A1A1A]/10 pt-4 flex justify-between items-center text-[8px] uppercase tracking-widest text-[#1A1A1A]/60">
                    <span className="font-mono">PLATE NO. 046 VOGUE REGISTRY</span>
                    <div className="flex space-x-0.5 opacity-20">
                      {[1, 4, 2, 8, 3, 9, 2, 5, 1, 7].map((b, i) => (
                        <div key={i} className="bg-black h-4" style={{ width: `${b}px` }} />
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>

          </div>
        ) : (
          /* STEP SUBMITTED: MAGNIFICENT BESPOKE INVITATION CERTIFICATE & REGISTRATION INVOICE */
          <div 
            className={`max-w-2xl mx-auto p-8 sm:p-14 space-y-8 relative animate-fade-in border ${
              aestheticMode === "gucci" 
                ? "bg-[#FCFAF7] border-4 border-[#8A7D6B] text-[#2C2421] shadow-2xl" 
                : "bg-white border-2 border-[#1A1A1A] text-[#1A1A1A]"
            }`} 
            id="submission-result-panel"
          >
            <div className="grain-overlay absolute inset-0 opacity-20"></div>

            {/* Gucci Ribbons Header decorative border */}
            {aestheticMode === "gucci" && (
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#11623E]" />
            )}

            {/* Certificate Header */}
            <div className="text-center pb-8 border-b border-[#1A1A1A]/10">
              <div className="inline-flex p-4 rounded-full border border-black/5 bg-[#F5F2ED] text-[#c4a482] mb-4">
                <CheckCircle size={40} className="animate-pulse" />
              </div>
              
              <h3 className="font-serif text-[34px] sm:text-[40px] font-extralight leading-none">
                Waitlist Registered
              </h3>
              
              <p className="text-[9px] uppercase tracking-[0.3em] text-[#c4a482] mt-3 font-semibold">
                Official Registry No. : {registryNumber || "TRP-2026"}
              </p>
            </div>

            {/* Receipt Summary Details */}
            <div className="space-y-6 text-xs text-[#1A1A1A]">
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#c4a482] font-semibold block text-center">
                Commission Particulars of Registry
              </span>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 font-light border-b border-[#1A1A1A]/10 pb-6 font-serif">
                <div>
                  <span className="block text-[8px] uppercase tracking-widest text-neutral-400 font-sans font-semibold">Commission Holder</span>
                  <span className="text-base font-bold italic">{form.fullName}</span>
                </div>
                <div>
                  <span className="block text-[8px] uppercase tracking-widest text-neutral-400 font-sans font-semibold">Significant Other</span>
                  <span className="text-base italic font-light">{form.partnerName}</span>
                </div>
                <div>
                  <span className="block text-[8px] uppercase tracking-widest text-neutral-400 font-sans font-semibold font-medium">Electronic Mail Coordinates</span>
                  <span className="font-mono text-[11px] font-semibold text-neutral-800">{form.email}</span>
                </div>
                <div>
                  <span className="block text-[8px] uppercase tracking-widest text-neutral-400 font-sans font-semibold">Wedding Date</span>
                  <span className="text-base font-mono font-medium">{form.weddingDate ? formatRoseDate(form.weddingDate) : "Not Selected"}</span>
                </div>
                <div>
                  <span className="block text-[8px] uppercase tracking-widest text-neutral-400 font-sans font-semibold">Wedding City & Country</span>
                  <span className="text-[#c4a482] font-semibold text-xs tracking-wider uppercase block mt-0.5">{form.weddingCityCountry || "Not Provided"}</span>
                </div>
                <div>
                  <span className="block text-[8px] uppercase tracking-widest text-neutral-400 font-sans font-semibold">Destination Locale & Venue</span>
                  <span className="text-neutral-800 font-semibold text-xs tracking-wider uppercase block mt-0.5">{form.destination || "Not Provided"}</span>
                </div>
                {form.plannerReferral && (
                  <div>
                    <span className="block text-[8px] uppercase tracking-widest text-neutral-400 font-sans font-semibold">Creative Wedding Planner</span>
                    <span className="text-xs italic text-stone-700 block mt-0.5">{form.plannerReferral}</span>
                  </div>
                )}
                {form.wardrobeDetails && (
                  <div>
                    <span className="block text-[8px] uppercase tracking-widest text-neutral-400 font-sans font-semibold">Planned Wardrobe & Styling</span>
                    <span className="text-xs text-stone-700 block mt-0.5">{form.wardrobeDetails}</span>
                  </div>
                )}
              </div>

              {/* How We Meet story */}
              {form.howWeMeet.trim() && (
                <div className="space-y-1.5 border-t border-[#1A1A1A]/5 pt-4">
                  <span className="text-[8px] uppercase tracking-widest text-[#c4a482] font-sans font-semibold">Our Story / How We Met</span>
                  <p className="bg-[#FAF9F6] border border-black/5 p-4 italic text-stone-700 font-serif text-[12px] leading-relaxed">
                    "{form.howWeMeet}"
                  </p>
                </div>
              )}

              {/* Photography Vision */}
              {form.photographyVision.trim() && (
                <div className="space-y-1.5 border-t border-[#1A1A1A]/5 pt-4">
                  <span className="text-[8px] uppercase tracking-widest text-[#4A1722] font-sans font-semibold">Photography Styling Vision</span>
                  <p className="bg-[#FAF9F6] border border-black/5 p-4 italic text-stone-700 font-serif text-[12px] leading-relaxed border-l-2 border-[#4A1722]">
                    "{form.photographyVision}"
                  </p>
                </div>
              )}

              {/* Instructions and Brief Message */}
              {form.atelierNotes.trim() && (
                <div className="space-y-1.5">
                  <span className="text-[8px] uppercase tracking-widest text-neutral-400 font-sans font-semibold">Client Directives & Message Notes Log</span>
                  <p className="bg-[#FAF9F6] border border-black/5 p-4 italic text-stone-700 font-serif text-[13px] leading-relaxed">
                    "{form.atelierNotes}"
                  </p>
                </div>
              )}
            </div>

            {/* Letterhead Note & Courier warning */}
            <div className="pt-6 border-t border-[#1A1A1A]/10 flex flex-col items-center justify-center space-y-5">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#c4a482] text-center max-w-sm leading-relaxed font-sans font-light">
                {aestheticMode === "gucci" 
                  ? "Our directors will review your visual styling parameters within seventy-two hours. If there is compatible artistic alignment, we will invite you to a private consultation." 
                  : "Registry coordinators will validate your coordinates. If a calendar vacancy matches your profile, you will receive an invitation to a private consultation."
                }
              </p>

              {/* Print and Restart Buttons */}
              <div className="flex space-x-3 w-full sm:w-auto">
                <button
                  id="btn-print-invoice"
                  onClick={() => window.print()}
                  className="flex-1 sm:flex-none border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-[10px] uppercase tracking-[0.2em] py-3 px-6 transition duration-500 flex items-center justify-center space-x-1 cursor-pointer font-sans bg-transparent"
                >
                  <Printer size={12} />
                  <span>Print Receipt File</span>
                </button>
                <button
                  id="btn-re-apply"
                  onClick={() => {
                    setStep(1);
                    setForm({
                      fullName: "",
                      email: "",
                      phone: "",
                      partnerName: "",
                      weddingDate: "",
                      destination: "",
                      guestScale: "intimate",
                      atelierNotes: "",
                      howWeMeet: "",
                      photographyVision: "",
                      weddingCityCountry: "",
                      plannerReferral: "",
                      wardrobeDetails: "",
                      smsTransactionalConsent: false,
                      smsMarketingConsent: false,
                    });
                    setSubmitError("");
                    setRegistryNumber("");
                  }}
                  className="flex-1 sm:flex-none bg-[#1A1A1A] text-white hover:bg-neutral-800 text-[10px] uppercase tracking-[0.2em] py-3 px-6 transition duration-500 flex items-center justify-center space-x-1 cursor-pointer font-sans border border-[#1A1A1A]"
                >
                  <RotateCcw size={12} />
                  <span>Submit New Form</span>
                </button>
              </div>
            </div>
            
          </div>
        )}

      </div>
    </section>
  );
}
